import { motion } from "framer-motion";
import { fadeUp, stagger } from "@/lib/anim";
import Reveal from "@/components/Reveal";
import {
  UserX,
  ShieldCheck,
  LayoutDashboard,
  BookOpen,
  FolderHeart,
  BarChart3,
} from "lucide-react";

const features = [
  {
    icon: UserX,
    title: "Anonymous Authentication",
    body: "Sign in and chat without ever revealing your identity. Your privacy is built into the system, not an afterthought.",
    span: "lg:col-span-3",
    feature: true,
  },
  {
    icon: ShieldCheck,
    title: "Moderated Messaging",
    body: "Every conversation runs through a secure, monitored channel so support stays safe and on track.",
    span: "lg:col-span-3",
  },
  {
    icon: LayoutDashboard,
    title: "Counsellor Dashboard",
    body: "Trained counsellors manage conversations, track wellbeing signals, and respond promptly through a dedicated workspace.",
    span: "lg:col-span-2",
  },
  {
    icon: BookOpen,
    title: "Resource Library",
    body: "Curated articles, guides, and coping tools available anytime — for anyone who isn't ready to talk yet.",
    span: "lg:col-span-2",
  },
  {
    icon: FolderHeart,
    title: "Case Management Tools",
    body: "Ensures continuity of care, so a student's history and needs are handled responsibly across sessions.",
    span: "lg:col-span-2",
  },
  {
    icon: BarChart3,
    title: "Administrative Analytics",
    body: "Anonymized, aggregate insights help institutions understand wellbeing trends without compromising individual privacy.",
    span: "lg:col-span-6",
    wide: true,
  },
];

export const Features = () => {
  return (
    <section
      id="features"
      className="py-24 md:py-40 px-6 md:px-12 bg-secondary/40 border-y border-border"
      data-testid="features-section"
    >
      <div className="mx-auto max-w-7xl">
        <div className="max-w-3xl mb-14 md:mb-20">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.24em] font-semibold text-primary mb-6">
              What we&apos;ve built
            </p>
          </Reveal>
          <Reveal delay={0.05}>
            <h2 className="font-serif font-light tracking-tight text-foreground text-3xl sm:text-4xl lg:text-5xl leading-[1.1] text-balance">
              Care that&apos;s careful — every layer designed to protect the student.
            </h2>
          </Reveal>
        </div>

        <motion.div
          variants={stagger(0.09)}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-60px" }}
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-5 md:gap-6"
        >
          {features.map((f) => {
            const Icon = f.icon;
            const highlight = f.feature;
            return (
              <motion.div
                key={f.title}
                variants={fadeUp}
                data-testid={`feature-card-${f.title.toLowerCase().replace(/\s+/g, "-")}`}
                className={`group relative rounded-3xl border p-8 md:p-10 transition-all duration-300 hover:-translate-y-1 ${
                  f.span
                } ${
                  highlight
                    ? "bg-primary text-primary-foreground border-primary"
                    : "bg-card border-border hover:border-primary/40 hover:shadow-md"
                } ${f.wide ? "flex flex-col md:flex-row md:items-center md:gap-10" : ""}`}
              >
                <div
                  className={`inline-flex h-12 w-12 items-center justify-center rounded-2xl mb-6 shrink-0 ${
                    highlight
                      ? "bg-primary-foreground/15 text-primary-foreground"
                      : "bg-accent/15 text-accent"
                  } ${f.wide ? "md:mb-0" : ""}`}
                >
                  <Icon className="h-6 w-6" strokeWidth={1.75} />
                </div>
                <div>
                  <h3
                    className={`font-serif text-2xl mb-3 tracking-tight ${
                      highlight ? "text-primary-foreground" : "text-foreground"
                    }`}
                  >
                    {f.title}
                  </h3>
                  <p
                    className={`leading-relaxed ${
                      highlight ? "text-primary-foreground/80" : "text-muted-foreground"
                    } ${f.wide ? "max-w-xl" : ""}`}
                  >
                    {f.body}
                  </p>
                </div>
              </motion.div>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
};

export default Features;
